const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Custom error class to preserve HTTP status
class APIError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "APIError";
  }
}

async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const url = `${API_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    credentials: "include", // Include cookies for session management
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "An error occurred" }));
    throw new APIError(
      error.detail || `HTTP ${response.status}`,
      response.status,
    );
  }

  return response.json();
}

// Partner Portal API Client
export const api = {
  // Authentication
  async getCurrentUser() {
    return fetchAPI("/api/portal/me");
  },

  async logout() {
    return fetchAPI("/auth/logout", { method: "POST" });
  },

  // Managed Clients
  async getManagedClients() {
    return fetchAPI("/api/portal/clients");
  },

  async getClientDetail(clientId: string) {
    return fetchAPI(`/api/portal/clients/${clientId}`);
  },

  // Client Users Management
  async createClientUser(
    clientId: string,
    userData: {
      email: string;
      name: string;
      role: string;
    },
  ) {
    return fetchAPI(`/api/portal/clients/${clientId}/users`, {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  // Client Connectors Management
  async configureConnector(
    clientId: string,
    connectorData: {
      connector_type: string;
      configuration: Record<string, unknown>;
    },
  ) {
    return fetchAPI(`/api/portal/clients/${clientId}/connectors`, {
      method: "POST",
      body: JSON.stringify(connectorData),
    });
  },

  // Client Billing
  async getClientBilling(clientId: string) {
    return fetchAPI(`/api/portal/clients/${clientId}/billing`);
  },

  async updateClientSubscription(
    clientId: string,
    subscriptionData: {
      plan: string;
      billing_cycle: string;
    },
  ) {
    return fetchAPI(`/api/portal/clients/${clientId}/billing/subscription`, {
      method: "PUT",
      body: JSON.stringify(subscriptionData),
    });
  },

  // Admin Access Elevation
  async requestAdminAccess(
    clientId: string,
    reason: string,
    requestedDurationHours: number,
  ) {
    return fetchAPI(`/api/portal/clients/${clientId}/request-admin`, {
      method: "POST",
      body: JSON.stringify({
        reason,
        requested_duration_hours: requestedDurationHours,
      }),
    });
  },

  async getCurrentAccessGrant(clientId: string) {
    return fetchAPI(`/api/portal/clients/${clientId}/access-grant`);
  },

  // Partner Billing (Phase 9)
  async getBillingOverview() {
    return fetchAPI("/api/portal/billing/overview");
  },

  async initiateStripeConnect() {
    return fetchAPI("/api/portal/billing/stripe-connect", {
      method: "POST",
    });
  },

  async getStripeConnectStatus() {
    return fetchAPI("/api/portal/billing/stripe-status");
  },

  async getRevenueBreakdown() {
    return fetchAPI("/api/portal/billing/revenue-breakdown");
  },

  async getPayoutHistory() {
    return fetchAPI("/api/portal/billing/payout-history");
  },

  async downloadPayoutInvoice(payoutId: string) {
    return fetchAPI(`/api/portal/billing/payout-invoice/${payoutId}`, {
      method: "POST",
    });
  },

  // Client Portal APIs (Unified Portal Extension)
  // Instance Management
  async getMyInstances() {
    return fetchAPI("/api/client/instances");
  },

  async createInstance(instanceData: {
    name: string;
    installation_type: "cloud" | "self-hosted";
    tier: "starter" | "pro" | "enterprise";
    odoo_url: string;
    odoo_api_key: string;
    odoo_database: string;
    selected_bobs: string[];
  }) {
    return fetchAPI("/api/client/instances", {
      method: "POST",
      body: JSON.stringify(instanceData),
    });
  },

  async getInstanceDetail(instanceId: string) {
    return fetchAPI(`/api/client/instances/${instanceId}`);
  },

  async updateInstance(
    instanceId: string,
    updates: {
      name?: string;
      odoo_url?: string;
    },
  ) {
    return fetchAPI(`/api/client/instances/${instanceId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },

  async deleteInstance(instanceId: string) {
    return fetchAPI(`/api/client/instances/${instanceId}`, {
      method: "DELETE",
    });
  },

  // BoB Management
  async getInstanceBoBs(instanceId: string) {
    return fetchAPI(`/api/client/instances/${instanceId}/bobs`);
  },

  async getBoBsForInstance(instanceId: string) {
    return fetchAPI(`/api/client/instances/${instanceId}/bobs`);
  },

  async toggleBoB(instanceId: string, bobId: string, enabled: boolean) {
    return fetchAPI(`/api/client/instances/${instanceId}/bobs/${bobId}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    });
  },

  // T133: Sync BoB registry for instance
  async syncBoBRegistry(instanceId: string) {
    return fetchAPI(
      `/api/client/instances/${instanceId}/orchestrator/sync-bobs`,
      {
        method: "POST",
      },
    );
  },

  // PROD-P0-002: Download connector installer
  async downloadConnectorInstaller(instanceId: string) {
    return fetchAPI(`/api/client/instances/${instanceId}/download`);
  },

  // Phase 12: BoB Marketplace APIs
  async getAvailableBoBs(params?: {
    category?: string | null;
    search?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.search) queryParams.append("search", params.search);
    const query = queryParams.toString();
    return fetchAPI(`/api/bobs/available${query ? `?${query}` : ""}`);
  },

  async batchInstallBoBs(instanceId: string, bobNames: string[]) {
    return fetchAPI("/api/bobs/batch-install", {
      method: "POST",
      body: JSON.stringify({
        instance_id: instanceId,
        bob_names: bobNames,
      }),
    });
  },

  // T134-T147: BoB Upgrade Management APIs
  async addBoBToInstance(instanceId: string, bobName: string) {
    return fetchAPI(`/api/instances/${instanceId}/bobs`, {
      method: "POST",
      body: JSON.stringify({ bob_name: bobName }),
    });
  },

  async checkBoBUpdates(instanceId: string) {
    return fetchAPI(`/api/instances/${instanceId}/bobs/updates`);
  },

  async upgradeBob(instanceId: string, bobName: string) {
    return fetchAPI(`/api/instances/${instanceId}/orchestrator/upgrade-bob`, {
      method: "POST",
      body: JSON.stringify({ bob_name: bobName }),
    });
  },

  async rollbackBob(instanceId: string, bobName: string) {
    return fetchAPI(`/api/instances/${instanceId}/orchestrator/rollback-bob`, {
      method: "POST",
      body: JSON.stringify({ bob_name: bobName }),
    });
  },

  async getBoBChangelog(bobName: string, version?: string) {
    const query = version ? `?version=${version}` : "";
    return fetchAPI(`/api/bobs/${bobName}/changelog${query}`);
  },

  async upgradeAllBobs(instanceId: string) {
    return fetchAPI(`/api/instances/${instanceId}/orchestrator/upgrade-all`, {
      method: "POST",
    });
  },

  // Subscription Management
  async getSubscription() {
    return fetchAPI("/api/client/subscription");
  },

  async updateSubscriptionTier(tier: "starter" | "pro" | "enterprise") {
    return fetchAPI("/api/client/subscription/tier", {
      method: "PUT",
      body: JSON.stringify({ tier }),
    });
  },

  // User Management
  async getMyUsers() {
    return fetchAPI("/api/client/users");
  },

  async inviteUser(userData: {
    email: string;
    role: "admin" | "user" | "viewer";
  }) {
    return fetchAPI("/api/client/users/invite", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  async removeUser(userId: string) {
    return fetchAPI(`/api/client/users/${userId}`, {
      method: "DELETE",
    });
  },

  // Solo Billing (Prepaid-only for direct customers)
  async getSoloPricingPlans() {
    return fetchAPI("/api/billing/solo-plans");
  },
};

export default api;
